import { cn } from '@/lib/utils';

interface GaugeChartProps {
  /** Value from 0 to 100 */
  value: number;
  /** Size of the gauge in pixels */
  size?: number;
  /** Label below the number */
  label?: string;
  /** Additional className */
  className?: string;
}

/**
 * Semicircular gauge chart for displaying percentage values.
 * Uses SVG arcs with color gradient based on value.
 */
export function GaugeChart({ value, size = 160, label, className }: GaugeChartProps) {
  const clampedValue = Math.max(0, Math.min(100, value));

  // SVG dimensions
  const strokeWidth = 12;
  const radius = (size - strokeWidth) / 2;
  const cx = size / 2;
  const cy = size / 2;

  // Color based on value
  const getColor = (v: number): string => {
    if (v >= 80) return 'var(--sime-success)';
    if (v >= 50) return 'var(--sime-warning)';
    if (v >= 25) return '#ff9f0a';
    return 'var(--sime-danger)';
  };

  const color = getColor(clampedValue);

  return (
    <div className={cn('flex flex-col items-center', className)}>
      <svg
        width={size}
        height={size / 2 + 12}
        viewBox={`0 0 ${size} ${size / 2 + 12}`}
        className="overflow-visible"
      >
        {/* Background arc */}
        <path
          d={describeArc(cx, cy, radius, 180, 360)}
          fill="none"
          stroke="var(--sime-shadow-dark)"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          opacity={0.25}
        />
        {/* Value arc */}
        {clampedValue > 0 && (
          <path
            d={describeArc(cx, cy, radius, 180, 180 + (clampedValue / 100) * 180)}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            style={{
              filter: `drop-shadow(0 0 4px ${color})`,
            }}
          />
        )}
        {/* Center number */}
        <text
          x={cx}
          y={cy - 4}
          textAnchor="middle"
          dominantBaseline="middle"
          className="font-bold"
          style={{
            fontSize: size * 0.2,
            fill: 'var(--sime-text-primary)',
          }}
        >
          {Math.round(clampedValue)}%
        </text>
      </svg>
      {label && (
        <span className="text-xs text-sime-text-tertiary mt-1">{label}</span>
      )}
    </div>
  );
}

/**
 * Describe an SVG arc path.
 * Angles in degrees, 0 = right (3 o'clock), going clockwise.
 * For a semicircle from left to right: startAngle=180, endAngle=360.
 */
function describeArc(
  cx: number,
  cy: number,
  radius: number,
  startAngle: number,
  endAngle: number,
): string {
  const startRad = ((startAngle - 90) * Math.PI) / 180;
  const endRad = ((endAngle - 90) * Math.PI) / 180;

  const x1 = cx + radius * Math.cos(startRad);
  const y1 = cy + radius * Math.sin(startRad);
  const x2 = cx + radius * Math.cos(endRad);
  const y2 = cy + radius * Math.sin(endRad);

  const largeArc = endAngle - startAngle > 180 ? 1 : 0;

  return `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`;
}
